import * as Yup from "yup";
import { getTextFromHtml } from "src/utils/getTextFromHtml";

export const MAX_LENGTH_VALIDATION = (max: number) =>
  Yup.string()
    .max(max, {
      i18nKey: "max-symbols",
      i18nParams: { count: max },
    })
    .trim();

export const LENGTH_VALIDATION = (min: number, max: number) =>
  Yup.string()
    .trim()
    .min(min, {
      i18nKey: "min-symbols",
      i18nParams: { count: min },
    })
    .max(max, {
      i18nKey: "max-symbols",
      i18nParams: { count: max },
    })
    .required("required");

export const EDITOR_LENGTH_VALIDATION = (
  min: number,
  max: number,
  optional: boolean = false
) =>
  Yup.mixed()
    .test({
      name: "min-editor-length-validation",
      message: {
        i18nKey: "min-symbols",
        i18nParams: { count: min },
      },
      test: (html: string) => {
        const text = getTextFromHtml(html);
        if (optional) return !text.length || text.length >= min;
        return text.length >= min;
      },
    })
    .test({
      name: "max-editor-length-validation",
      message: {
        i18nKey: "max-symbols",
        i18nParams: { count: max },
      },
      test: (html: string) => {
        const text = getTextFromHtml(html);
        if (optional) return !text.length || text.length <= max;
        return text.length <= max;
      },
    });

export const EMAIL_VALIDATION_SCHEMA = LENGTH_VALIDATION(3, 100)
  .email("incorrect-email")
  .required("required");

export const PHONE_VALIDATION_SCHEMA = LENGTH_VALIDATION(3, 20).matches(
  /^(\+?[1-9]{1,4}[ -]*|\([0-9]{2,3}\)[ -]*|[0-9]{2,4}[ -]*)*?[0-9]{3,4}[ -]*[0-9]{3,4}([ -]*x[0-9]+)?$/,
  "incorrect-phone-number"
);

export const PASSWORD_VALIDATION_SCHEMA = LENGTH_VALIDATION(3, 100)
  .matches(
    /^(?=.*[0-9])|(?=.*[!@#$%^&*(),.?":{}|<>]).*$/,
    "contain-numeric-or-special-character"
  )
  .matches(/[0-9]/, "include-number")
  .matches(/[a-z]/, "include-lowercase-letter")
  .matches(/[A-Z]/, "include-uppercase-letter");

export const FIRST_NAME_VALIDATION_SCHEMA = LENGTH_VALIDATION(3, 50).matches(
  /^[A-Za-z\s]+$/,
  "not-valid-name"
);

export const LAST_NAME_VALIDATION_SCHEMA = LENGTH_VALIDATION(3, 100).matches(
  /^[A-Za-z\s]+$/,
  "not-valid-name"
);

export const WEBSITE_LINK_VALIDATION_SCHEMA =
  Yup.string().url("url-is-invalid");

export const NOT_EMPTY_ARRAY_VALIDATION = Yup.array().min(1, "required");

const GOOGLE_BUSINESS_PROFILE_LINK_REGEX =
  /https:\/\/(?:g\.co\/kgs\/[a-zA-Z0-9]+|maps\.app\.goo\.gl\/[a-zA-Z0-9]+)/;

export const GOOGLE_BUSINESS_PROFILE_LINK_VALIDATION_SCHEMA = Yup.string()
  .matches(GOOGLE_BUSINESS_PROFILE_LINK_REGEX, "incorrect-google-profile-link")
  .optional();

export const GREATER_THAN_ZERO_VALIDATION = Yup.number()
  .min(0, "greater-than-zero")
  .required("required");

export const MIN_MAX_FIELDS_VALIDATION = (
  minFieldName: string,
  maxFieldName: string
) => ({
  min: GREATER_THAN_ZERO_VALIDATION.lessThan(
    Yup.ref(maxFieldName),
    "less-than-max"
  ),
  max: GREATER_THAN_ZERO_VALIDATION.moreThan(
    Yup.ref(minFieldName),
    "greater-than-min"
  ),
});

const BYTES_IN_MB = 1048576;

export const FILE_SIZE_VALIDATION = (maxSizeMB: number) =>
  Yup.mixed()
    .test({
      name: "validateFileSize",
      message: {
        i18nKey: "file-size-exceeded",
        i18nParams: { maxSizeMB },
      },
      test: (file: File) =>
        Boolean(file) && file.size <= maxSizeMB * BYTES_IN_MB,
    })
    .required("required");

export const VALIDATE_FILES_SIZE_AND_COUNT = (
  maxSizeMB: number,
  maxFileCount: number
) =>
  NOT_EMPTY_ARRAY_VALIDATION.test({
    name: "validateFileSize",
    message: {
      i18nKey: "files-size-exceeded",
      i18nParams: { maxSizeMB },
    },
    test: (files: File[]) =>
      files.every((f) => f.size <= maxSizeMB * BYTES_IN_MB),
  }).test({
    name: "validateFileCount",
    message: {
      i18nKey: "file-count-exceeded",
      i18nParams: { maxFileCount },
    },
    test: (files: File[]) => files.length <= maxFileCount,
  });

export const VALIDATE_FILES_SIZE_AND_COUNT_OPTIONAL = (
  maxSizeMB: number,
  maxFileCount: number
) =>
  Yup.mixed()
    .test({
      name: "validateFileSize",
      message: {
        i18nKey: "files-size-exceeded",
        i18nParams: { maxSizeMB },
      },
      test: (files: File[]) =>
        !Boolean(files.length) ||
        files.every((f) => f.size <= maxSizeMB * BYTES_IN_MB),
    })
    .test({
      name: "validateFileCount",
      message: {
        i18nKey: "file-count-exceeded",
        i18nParams: { maxFileCount },
      },
      test: (files: File[]) => files.length <= maxFileCount,
    });

export const FILE_SIZE_VALIDATION_OPTIONAL = (maxSizeMB: number) =>
  Yup.mixed().test({
    name: "validateFileSize",
    message: {
      i18nKey: "file-size-exceeded",
      i18nParams: { maxSizeMB },
    },
    test: (file: File) =>
      !Boolean(file) || file.size <= maxSizeMB * BYTES_IN_MB,
  });

export const REPEAT_NEW_PASSWORD_VALIDATION_SCHEMA = Yup.string()
  .required("required")
  .oneOf([Yup.ref("newPassword"), null], "passwords-must-match");

export const POSITIVE_NUMBER_VALIDATION_SCHEMA = Yup.number()
  .required("required")
  .positive("positive-number");
